/**
 * Hook pour le paiement public de factures
 * Utilise TanStack Query pour la gestion des données serveur
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { publicInvoicesAPI } from '@/utils/api';
import type {
  PaymentOptions,
  StripePaymentResult,
  PayPalOrderResult,
  PayPalCaptureResult,
} from '../types/payment.types';

const PAYMENT_OPTIONS_KEY = ['public-payment-options'];

/**
 * Hook pour récupérer les options de paiement d'une facture
 */
export function usePaymentOptions(token: string | undefined) {
  return useQuery<{ success: boolean; data: PaymentOptions }>({
    queryKey: [...PAYMENT_OPTIONS_KEY, token],
    queryFn: () => publicInvoicesAPI.getPaymentOptions(token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

/**
 * Hook pour initier un paiement Stripe
 */
export function useInitiateStripePayment() {
  return useMutation<{ success: boolean; data: StripePaymentResult }, Error, string>({
    mutationFn: (token: string) => publicInvoicesAPI.initiateStripePayment(token),
  });
}

/**
 * Hook pour initier un paiement PayPal
 */
export function useInitiatePayPalPayment() {
  return useMutation<
    { success: boolean; data: PayPalOrderResult },
    Error,
    { token: string; returnUrl: string; cancelUrl: string }
  >({
    mutationFn: ({ token, returnUrl, cancelUrl }) =>
      publicInvoicesAPI.initiatePayPalPayment(token, returnUrl, cancelUrl),
  });
}

/**
 * Hook pour capturer un paiement PayPal
 */
export function useCapturePayPalPayment() {
  return useMutation<
    { success: boolean; data: PayPalCaptureResult },
    Error,
    { token: string; orderId: string }
  >({
    mutationFn: ({ token, orderId }) =>
      publicInvoicesAPI.capturePayPalPayment(token, orderId),
  });
}
